import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { MapPin, Phone, Mail, FileText, ExternalLink } from 'lucide-react';
import { contactInfo } from '../mockData';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Hair Extensions Training Academy",
    "description": "Get in touch with Hair Extensions Training Academy for course enquiries and information.",
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "Hair Extensions Training Academy",
      "telephone": "+44 20 3051 4338",
      "email": "info@hairextensionstraining.academy",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "GB",
        "addressLocality": "United Kingdom"
      },
      "openingHours": "Mo-Fr 09:00-17:00",
      "priceRange": "££"
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock submission - will be replaced with actual API call
    setTimeout(() => {
      toast({
        title: "Message Sent!",
        description: "Thank you for contacting us. We'll get back to you soon.",
      });
      setIsSubmitting(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    }, 1000);
  };

  return (
    <div className="page-container">
      <Helmet>
        <title>Contact Us | Hair Extensions Training Academy</title>
        <meta name="description" content="Contact Hair Extensions Training Academy for course enquiries. Phone: +44 20 3051 4338. Email: info@hairextensionstraining.academy" />
        <link rel="canonical" href="https://hairextensionstraining.academy/contact" />
        <script type="application/ld+json">{JSON.stringify(contactSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="contact-hero">
        <div className="content-wrapper">
          <h1 className="page-title">Contact Us</h1>
          <p className="page-subtitle">
            Get in touch with our team to learn more about our courses
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-container">
        <div className="content-wrapper">
          <div className="contact-grid">
            {/* Contact Info */}
            <div className="contact-info-section">
              <h2 className="section-title">Get In Touch</h2>
              <p className="section-text">
                Have questions about our courses or want to learn more about becoming a hair extension specialist?
                We're here to help!
              </p>

              <div className="contact-details">
                <div className="contact-detail-item">
                  <MapPin className="contact-icon" size={24} />
                  <div>
                    <h3 className="contact-detail-title">Location</h3>
                    <p className="contact-detail-text">{contactInfo.address}</p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <Phone className="contact-icon" size={24} />
                  <div>
                    <h3 className="contact-detail-title">Phone</h3>
                    <p className="contact-detail-text">{contactInfo.phone}</p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <Mail className="contact-icon" size={24} />
                  <div>
                    <h3 className="contact-detail-title">Email</h3>
                    <p className="contact-detail-text">{contactInfo.email}</p>
                  </div>
                </div>

                <div className="contact-detail-item pre-enrolment-link">
                  <FileText className="contact-icon" size={24} />
                  <div>
                    <h3 className="contact-detail-title">Pre Enrolment Form</h3>
                    <a 
                      href="https://bit.ly/3YwunSy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="enrolment-link"
                    >
                      Complete Pre Enrolment Form <ExternalLink size={16} />
                    </a>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="map-container">
                <iframe
                  width="100%"
                  height="300"
                  frameBorder="0"
                  style={{ border: 0 }}
                  src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2426.9876543210!2d${contactInfo.lng}!3d${contactInfo.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTLCsDMwJzI5LjIiTiAywrAwNScwNS4wIlc!5e0!3m2!1sen!2suk!4v1234567890123`}
                  allowFullScreen
                  title="Dudley Training Centre Location"
                ></iframe>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-section">
              <h2 className="section-title">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="+44 7XXX XXXXXX"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject" className="form-label">Subject *</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="What is your enquiry about?"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="form-textarea"
                    placeholder="Tell us more about your enquiry..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-submit"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;